import React, { useContext, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { VariantContextData } from "../variants/VariantBuilderContext";
import VariantBuilderSetting from "../variants/VariantBuilderSetting";
import VariantBuilderPanelCollapse from "../variants/VariantBuilderPanelCollapse";

const ElementSettingsVideo = (props) => {
  const [contextData, builder, setContextData] = useContext(VariantContextData);
  let website = contextData.objects.website;
  let cta = contextData.objects.cta;
  let variant = contextData.objects.variant;
  let element = builder.selected().object;
  let section = variant.sections[element.section_id];
  let step = variant.steps[section.step_id];

  return (
    <React.Fragment>
      {element.element_type == "video" && (
        <React.Fragment>
          <VariantBuilderSetting
            object={element}
            label="Video URL"
            setting_name={"[options][video-embed-url]"}
            setting_type="text"
            object_type="elements"
            object_id={element.id}
            placeholder="https://www.youtube.com/watch?v=..."
            value={
              element.options["video-embed-url"]
                ? element.options["video-embed-url"]
                : ""
            }
            condition={element.element_type == "video"}
            warning={
              !element.options["video-embed-url"]
                ? "Video URL required"
                : undefined
            }
            note="YouTube, Vimeo, Wistia URL or iframe source"
          />

          <VariantBuilderSetting
            object={element}
            label={"Controls"}
            setting_name={"[options][video-embed-controls]"}
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["video-embed-controls"]
                ? element.options["video-embed-controls"]
                : "true"
            }
            condition={
              element.element_type == "video" &&
              element.options["video-embed-url"] &&
              element.options["video-embed-url"].indexOf("youtube") > -1
            }
            col="4"
          />

          <VariantBuilderSetting
            object={element}
            label={"Autoplay"}
            setting_name={"[options][video-embed-autoplay]"}
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["video-embed-autoplay"]
                ? element.options["video-embed-autoplay"]
                : "false"
            }
            condition={element.element_type == "video"}
            col="4"
          />

          <VariantBuilderSetting
            object={element}
            label={"Loop"}
            setting_name={"[options][video-embed-loop]"}
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["video-embed-loop"]
                ? element.options["video-embed-loop"]
                : "false"
            }
            condition={
              element.element_type == "video" &&
              element.options["video-embed-url"] &&
              (element.options["video-embed-url"].indexOf("youtube") > -1 ||
                element.options["video-embed-url"].indexOf("vimeo") > -1)
            }
            col="4"
          />

          <VariantBuilderSetting
            object={element}
            label={"Muted"}
            setting_name={"[options][video-embed-start-muted]"}
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["video-embed-start-muted"]
                ? element.options["video-embed-start-muted"]
                : "false"
            }
            condition={
              element.element_type == "video" &&
              element.options["video-embed-url"] &&
              (element.options["video-embed-url"].indexOf("youtube") > -1 ||
                element.options["video-embed-url"].indexOf("vimeo") > -1) &&
              element.options["video-embed-autoplay"] == "true"
            }
            tooltip="Browser will not autoplay videos if user hasn't interacted with the page and this is unchecked."
            col="4"
          />

          <VariantBuilderSetting
            object={element}
            label={"Show Related From YouTube"}
            setting_name={"[options][video-embed-related]"}
            setting_type="checkbox"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["video-embed-related"]
                ? element.options["video-embed-related"]
                : "false"
            }
            condition={
              element.element_type == "video" &&
              element.options["video-embed-url"] &&
              element.options["video-embed-url"].indexOf("youtube") > -1
            }
            tooltip="Defaults to related videos being limited to the video's channel. Toggle to allow other channels videos to be recommended."
            col={12}
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][video-auto-advance]"
            setting_type="dropdown"
            label="Auto advance"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["video-auto-advance"]
                ? element.options["video-auto-advance"]
                : "none"
            }
            options={[
              { value: "none", text: "None" },
              { value: "done", text: "When video done" },
              { value: "seconds", text: "After X seconds" },
            ]}
            condition={element.element_type == "video"}
            tooltip={
              element.options["video-auto-advance"] == "done" &&
              element.options["video-embed-loop"] == "true"
                ? "Loop should be off for 'When video done' to work reliably."
                : "Jump to another funnel step when the video finishes or after a delay."
            }
            note={
              element.options["video-auto-advance"] == "done"
                ? "Supported for YouTube, Vimeo, and Wistia embeds."
                : undefined
            }
          />

          <VariantBuilderSetting
            object={element}
            setting_name="[options][video-auto-advance-delay]"
            setting_type="range"
            object_type="elements"
            object_id={element.id}
            label="Auto advance after (seconds)"
            value={
              element.options["video-auto-advance-delay"]
                ? element.options["video-auto-advance-delay"]
                : "5"
            }
            condition={
              element.element_type == "video" &&
              element.options["video-auto-advance"] == "seconds"
            }
            min={1}
            max={600}
            range_step={"1"}
            placeholder={"5"}
          />

          <VariantBuilderSetting
            object={element}
            label="Jump to funnel step"
            setting_name="[options][video-auto-advance-step]"
            setting_type="dropdown"
            object_type="elements"
            object_id={element.id}
            value={
              element.options["video-auto-advance-step"]
                ? element.options["video-auto-advance-step"]
                : ""
            }
            options={[
              {
                value: "",
                text: "Select a funnel step",
                disabled: true,
              },
              ...Object.values(variant.steps)
                .filter((s) => s.id !== step.id && s.toBeDeleted !== true)
                .sort((a, b) => {
                  return a.position - b.position;
                })
                .map((s) => {
                  return {
                    value: s.id + "",
                    text:
                      "Step " +
                      s.position +
                      (s.options["name"] ? " - " + s.options["name"] : ""),
                  };
                }),
            ]}
            condition={
              element.element_type == "video" &&
              ["done", "seconds"].includes(
                element.options["video-auto-advance"]
              )
            }
            warning={
              !element.options["video-auto-advance-step"]
                ? "Step to jump to not yet set"
                : undefined
            }
          />
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

export default ElementSettingsVideo;
